import React, { type PropsWithChildren } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { NavErrorBoundary } from '@atlassian/navigation-error-boundary/ui';

type AppChromeErrorBoundaryProps = PropsWithChildren<{
	/** The app GlobalRoot renders in; tags the failure in Sentry. */
	app: string;
}>;

/**
 * Keeps an app chrome failure local to the chrome. `AppChrome` calls the
 * consumer's `useRelayQuery()`, and when that throws the error would otherwise
 * unwind past `Root` to the consumer's boundary, taking the page content down
 * with it and leaving the failure unattributed to navigation. Here the chrome
 * renders nothing and the error is reported the way the rest of the navigation
 * reports render failures: to navigation's Sentry under `location`, plus the
 * boundary's standard `errorBoundary unhandledErrorCaught` event.
 */
export function AppChromeErrorBoundary({
	app,
	children,
}: AppChromeErrorBoundaryProps): React.JSX.Element {
	if (!fg('navx-5874-app-chrome-error-boundary')) {
		return <>{children}</>;
	}

	return (
		<NavErrorBoundary location="global-root-app-chrome" sentryConfig={{ product: app }} silent>
			{children}
		</NavErrorBoundary>
	);
}
